import React, { useEffect, useState } from 'react';
import { sendContactMessage } from '../lib/auth';

const SUBJECT_OPTIONS = [
  { value: '', label: 'Select a subject...' },
  { value: 'submission-inquiry', label: 'Submission Inquiry' },
  { value: 'peer-review', label: 'Peer Review Query' },
  { value: 'editorial-board', label: 'Editorial Board Inquiry' },
  { value: 'technical-issue', label: 'Technical Issue' },
  { value: 'permissions', label: 'Permissions & Licensing' },
  { value: 'general', label: 'General Inquiry' },
];

function Contact() {
  useEffect(() => {
    document.title = 'Contact — JAMSD';
  }, []);

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.email.trim()) {
      e.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = 'Please enter a valid email address.';
    }
    if (!form.subject) e.subject = 'Please select a subject.';
    if (!form.message.trim()) e.message = 'Message is required.';
    else if (form.message.trim().length < 20) e.message = 'Message must be at least 20 characters.';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setApiError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitting(true);
    setApiError('');
    try {
      await sendContactMessage(form);
      setSubmitted(true);
    } catch (err) {
      setApiError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm({ name: '', email: '', subject: '', message: '' });
    setErrors({});
    setApiError('');
    setSubmitted(false);
  };

  const inputClass = (field) =>
    `w-full font-sans text-sm border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber focus:border-transparent transition-colors ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-bg-border bg-white'
    }`;

  return (
    <>
      <section className="bg-navy pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-4 items-start">
            <div className="w-1 bg-amber rounded-full self-stretch" aria-hidden="true" />
            <div>
              <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest mb-2">Get in Touch</p>
              <h1 className="font-display text-white text-3xl md:text-4xl font-bold mb-2">Contact Us</h1>
              <p className="font-sans text-slate-light text-base max-w-2xl">Have a question or inquiry? We'd love to hear from you.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* Left: Contact form */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <h2 className="section-heading mb-6">Send Us a Message</h2>

              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-display text-navy text-xl font-bold mb-2">Message Sent!</h3>
                  <p className="font-sans text-gray-600 text-sm leading-relaxed mb-2">
                    Thank you for reaching out, <strong>{form.name}</strong>. We have received your message and will get back to you at <strong>{form.email}</strong> within 2–3 business days.
                  </p>
                  <p className="font-sans text-gray-500 text-xs mb-5">A confirmation has been sent to your email address.</p>
                  <button type="button" onClick={handleReset} className="btn-amber-outline text-sm">
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  {apiError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{apiError}</div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block font-sans text-sm font-semibold text-navy mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text" id="name" name="name"
                        value={form.name} onChange={handleChange}
                        placeholder="Your full name"
                        className={inputClass('name')}
                        autoComplete="name"
                      />
                      {errors.name && <p className="font-sans text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    <div>
                      <label htmlFor="email" className="block font-sans text-sm font-semibold text-navy mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email" id="email" name="email"
                        value={form.email} onChange={handleChange}
                        placeholder="your@email.com"
                        className={inputClass('email')}
                        autoComplete="email"
                      />
                      {errors.email && <p className="font-sans text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block font-sans text-sm font-semibold text-navy mb-1.5">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="subject" name="subject"
                      value={form.subject} onChange={handleChange}
                      className={`${inputClass('subject')} cursor-pointer`}
                    >
                      {SUBJECT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value} disabled={!opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {errors.subject && <p className="font-sans text-red-500 text-xs mt-1">{errors.subject}</p>}
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-sans text-sm font-semibold text-navy mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message" name="message"
                      value={form.message} onChange={handleChange}
                      rows={6} placeholder="Write your message here..."
                      className={`${inputClass('message')} resize-vertical min-h-[120px]`}
                    />
                    {errors.message && <p className="font-sans text-red-500 text-xs mt-1">{errors.message}</p>}
                    <p className="font-sans text-xs text-slate mt-1 text-right">{form.message.length} characters</p>
                  </div>

                  <div>
                    <button type="submit" disabled={submitting} className="btn-amber w-full sm:w-auto px-8 py-3 text-base disabled:opacity-60">
                      {submitting ? 'Sending…' : 'Send Message'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Right: Editorial office details */}
          <div className="lg:w-80 xl:w-96 flex-shrink-0 space-y-6">
            <div className="bg-navy rounded-lg p-6 text-white">
              <h3 className="font-display text-amber text-lg font-bold mb-4">Editorial Office</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="font-sans text-slate-pale text-xs font-semibold uppercase tracking-wide mb-1">Email</p>
                    <a href="mailto:jamsd@editor.com" className="font-sans text-amber hover:text-white transition-colors font-semibold block">jamsd@editor.com</a>
                    <a href="mailto:jamsd@admin.com" className="font-sans text-amber hover:text-white transition-colors font-semibold block">jamsd@admin.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 3h4l2 5-2.5 1.5a11 11 0 005 5L12 12l5 2v4a2 2 0 01-2 2A16 16 0 012 5a2 2 0 012-2z" />
                  </svg>
                  <div>
                    <p className="font-sans text-slate-pale text-xs font-semibold uppercase tracking-wide mb-1">Phone</p>
                    <a href="tel:+919236719340" className="font-sans text-amber hover:text-white transition-colors font-semibold">+91-9236719340</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-sans text-slate-pale text-xs font-semibold uppercase tracking-wide mb-1">Response Time</p>
                    <p className="font-sans text-slate-light text-sm">2–3 business days for general inquiries. 48 hours for submission confirmations.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-5">
              <h3 className="font-display text-navy font-semibold text-base mb-3">Journal Details</h3>
              <div className="space-y-2 font-sans text-sm">
                {[
                  ['Journal', 'JAMSD'],
                  ['ISSN', 'XXXX-XXXX'],
                  ['Frequency', 'Biannual'],
                  ['Access', 'Open Access'],
                  ['APC', 'None'],
                ].map(([label, value], i) => (
                  <div key={label} className={`flex justify-between gap-2 ${i > 0 ? 'border-t border-bg-border pt-2' : ''}`}>
                    <span className="text-slate">{label}</span>
                    <span className={`font-semibold ${label === 'Access' ? 'text-amber' : label === 'APC' ? 'text-green-700' : 'text-navy'}`}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-amber-tint border border-amber rounded-lg p-5">
              <h3 className="font-display text-navy font-bold text-base mb-2">Submit a Manuscript</h3>
              <p className="font-sans text-amber-tinttext text-sm mb-3 leading-relaxed">Ready to submit your research? Review our author guidelines first.</p>
              <div className="flex flex-col gap-2">
                <a href="/submit" className="btn-amber text-sm text-center">Submit Manuscript</a>
                <a href="/author-guidelines" className="btn-amber-outline text-sm text-center">Author Guidelines</a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

export default Contact;
